import { isAxiosError } from "axios";
import { AlertType } from "@/enumerations/Alerts/AlertType";
import type Alert from "@/interfaces/Alerts/Alert";
import type LastLogin from "@/interfaces/WebLogin/LastLogin";
import type RepositoryResponse from "@/interfaces/Repository/RepositoryResponse";
import type RepositoryRequestResponse from "@/interfaces/Repository/Requests/RepositoryRequestResponse";
import type WebLoginAuthentication from "@/interfaces/WebLogin/WebLoginAuthentication";
import type WebLoginAuthenticationTokens from "@/interfaces/WebLogin/WebLoginAuthenticationTokens";
import RepositoryRequest from "@/helpers/Repositories/RepositoryRequest";
import { portalHttpClient } from "@/services/httpClient";
import { useAlertsStore } from "@/stores/alerts";

const END_POINT = "api/customerPortal/v1/webLogin";

export async function login(
  username: string,
  password: string,
  keepLoggedIn: boolean
): Promise<WebLoginAuthentication> {
  const response = await portalHttpClient.post(
    `${END_POINT}/login`,
    {
      keepLoggedIn,
    },
    {
      auth: {
        username,
        password,
      },
    }
  );

  return response.data as WebLoginAuthentication;
}

export async function getLogins(): Promise<LastLogin[]> {
  const response = await portalHttpClient.get(`${END_POINT}/accountAudit`);
  return response.data as LastLogin[];
}

export async function setAccountLastLogin(customerId: string): Promise<void> {
  await portalHttpClient.put(`${END_POINT}/accountAudit`, {
    customerId,
  });
}

export async function resetPasswordWithToken(
  token: string,
  username: string,
  newPassword: string
): Promise<RepositoryResponse> {
  const response = await portalHttpClient.put(`${END_POINT}/resetPasswordToken`, {
    resetToken: token,
    loginId: username,
    newPassword,
  });

  return response as unknown as RepositoryResponse;
}

export async function resetPassword(
  jsonWebToken: string,
  oldPassword: string,
  newPassword: string
): Promise<WebLoginAuthentication | undefined> {
  try {
    const response = await portalHttpClient.post(`${END_POINT}/resetPassword`, {
      jsonWebToken,
      oldPassword,
      newPassword,
    });

    return response.data as WebLoginAuthentication;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      // The json web token expired before the user submitted the password reset.
      // Tokens are not allowed to be refreshed when a password reset is required.
      useAlertsStore().setAlert({
        headerMessage: "Session timeout",
        bodyMessage: "Your password was not updated",
        type: AlertType.Error,
      } as Alert);
    }

    return undefined;
  }
}

export async function getRefreshToken(
  jsonWebToken: string,
  refreshToken: string
): Promise<WebLoginAuthenticationTokens> {
  const response = await portalHttpClient.post(`${END_POINT}/refreshToken`, {
    jsonWebToken,
    refreshToken,
  });

  return response.data as WebLoginAuthenticationTokens;
}

export async function forgotPassword(
  emailaddress: string,
  username: string
): Promise<void> {
  await portalHttpClient.post(`${END_POINT}/forgotPassword`, {
    email: emailaddress,
    username,
  });
}

export async function forgotUsername(emailaddress: string): Promise<void> {
  await portalHttpClient.post(
    `${END_POINT}/forgotUsername`,
    `"${emailaddress}"`,
    { headers: { "Content-type": "application/json" } }
  );
}

export async function verifyCustomerByReceipt(
  customerId: string,
  storeNumber: string,
  invoiceNumber: string
): Promise<RepositoryRequestResponse> {
  const getRequestParameters = {
    customerId,
    storeNumber,
    invoiceNumber,
  };

  return await new RepositoryRequest()
    .WithUrl(`${END_POINT}/lookup`)
    .WithFilter(getRequestParameters)
    .Get();
}

export async function createWebLogin(
  username: string,
  displayName: string,
  emailaddress: string,
  storeNumber: string,
  accountNumber: string,
  invoiceNumber: string
): Promise<RepositoryRequestResponse> {
  return await new RepositoryRequest().WithUrl(END_POINT).Post({
    username,
    displayName,
    email: emailaddress,
    locationId: storeNumber,
    accountId: accountNumber,
    invoiceNumber,
  });
}
